import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  buildPartnerGroupChannelMessageMapOptions,
  mapPartnerGroupChannelMessagesPage,
  mapPartnerGroupChannelResponse,
  PARTNER_GROUP_CHANNEL_MEMBER_PAGE_SIZE,
} from '../components/Partner/Community/mapPartnerGroupChannel';
import { mapPartnerGroupChannelJoinRequestsResponse } from '../components/Partner/Community/mapPartnerGroupChannelJoinRequests';
import {
  getPartnerGroupChannel,
  getPartnerGroupChannelJoinRequests,
  getChannelMessages,
} from '../services/partnerGroupChannel';

import { partnerGroupChannelQueryKeys } from './partner-group-channel-query-keys';

export { partnerGroupChannelQueryKeys };

/**
 * Partner community channel details with paginated members
 * (GET /admin/partner/group-channel?memberLimit=&memberOffset=).
 */
export const usePartnerGroupChannelQuery = (options = {}) => {
  const { enabled = true, memberLimit = PARTNER_GROUP_CHANNEL_MEMBER_PAGE_SIZE } = options;
  const { isPartner } = useAuth();
  const normalizedLimit = Math.min(Math.max(Number(memberLimit) || 50, 1), 100);

  return useInfiniteQuery({
    queryKey: partnerGroupChannelQueryKeys.detail(normalizedLimit),
    enabled: enabled && isPartner,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const result = await getPartnerGroupChannel({
        memberLimit: normalizedLimit,
        memberOffset: pageParam ?? 0,
      });
      const mapped = mapPartnerGroupChannelResponse(result);

      if (mapped.status === 'error') {
        throw new Error(result.message || 'Failed to load community channel');
      }

      return mapped;
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.channel?.membersPagination;
      if (!pagination?.hasMore) {
        return undefined;
      }
      const offset = Number(pagination.offset);
      const limit = Number(pagination.limit);
      if (!Number.isFinite(offset) || !Number.isFinite(limit) || limit <= 0) {
        return undefined;
      }
      return offset + limit;
    },
    staleTime: 30_000,
  });
};

export const usePartnerGroupChannelMessagesQuery = (options = {}) => {
  const { enabled = true, limit = 20, channelId = null, resolvedAt = null } = options;
  const { isAdmin, firebaseUid, partnerProfile, adminProfile } = useAuth();
  const normalizedChannelId = channelId ? String(channelId).trim() : '';
  const normalizedResolvedAt = resolvedAt ? String(resolvedAt).trim() : '';
  const messageMapOptions = useMemo(
    () =>
      buildPartnerGroupChannelMessageMapOptions({
        firebaseUid,
        partnerProfile: isAdmin ? adminProfile : partnerProfile,
      }),
    [adminProfile, firebaseUid, isAdmin, partnerProfile],
  );

  return useInfiniteQuery({
    queryKey: partnerGroupChannelQueryKeys.messages(
      limit,
      normalizedChannelId || null,
      normalizedResolvedAt || null,
    ),
    enabled: Boolean(enabled) && Boolean(normalizedChannelId),
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const result = await getChannelMessages({
        limit,
        next: pageParam ?? undefined,
        channelId: normalizedChannelId,
        ...(normalizedResolvedAt ? { resolvedAt: normalizedResolvedAt } : {}),
      });
      const mapped = mapPartnerGroupChannelMessagesPage(result, messageMapOptions);

      if (mapped.status === 'error') {
        throw new Error(result.message || 'Failed to load community messages');
      }

      return mapped;
    },
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.hasMore ? (lastPage?.pagination?.next ?? undefined) : undefined,
    staleTime: 15_000,
  });
};

export const usePartnerGroupChannelJoinRequestsQuery = (options = {}) => {
  const { enabled = true } = options;
  const { isPartner } = useAuth();

  return useQuery({
    queryKey: partnerGroupChannelQueryKeys.joinRequests(),
    enabled: enabled && isPartner,
    queryFn: async () => {
      const result = await getPartnerGroupChannelJoinRequests();
      const mapped = mapPartnerGroupChannelJoinRequestsResponse(result);

      if (mapped.status === 'error' || mapped.status === 'forbidden') {
        throw new Error(result.message || 'Failed to load community join requests');
      }

      return mapped;
    },
    staleTime: 15_000,
  });
};
